import { Helmet } from 'react-helmet-async';
import { useState, useCallback, type MouseEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import SwapVertIcon from '@mui/icons-material/SwapVert';

// @mui
import {
	Card,
	Table,
	Stack,
	Paper,
	Button,
	Popover,
	TableRow,
	LinearProgress,
	MenuItem,
	TableBody,
	TableCell,
	Container,
	Typography,
	IconButton,
	TableContainer,
	TablePagination,
} from '@mui/material';
// components
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import Label from '../../components/label';
import { ListHead, ListToolbar, type HeadCell } from '../../components/list';
import { fCurrency } from '../../utils/formatNumber';

import { useGetTestsQuery, useDelTestMutation } from '../../slices/apis/testApi';
import { applySortFilter, getComparator, type Order } from '../../utils/tableSorter';
import type { TestProps } from '../../models';
import { useRole } from '../../hooks/useRole';

// ----------------------------------------------------------------------

const TABLE_HEAD: HeadCell[] = [
	{ id: 'id', label: 'ID', align: 'left' },
	{ id: 'name', label: 'Name', align: 'left' },
	{ id: 'description', label: 'Description', align: 'left' },
	{ id: 'price', label: 'Price', align: 'left' },
	{ id: 'isBulk', label: '', align: 'left' },
	{ id: '', label: '', align: 'right' },
];

// ----------------------------------------------------------------------

export default function Tests() {
	const { isAdmin } = useRole();
	const [menuOpen, setMenuOpen] = useState<HTMLElement | null>(null);
	const [page, setPage] = useState<number>(0);
	const [order, setOrder] = useState<Order>('asc');
	const [selected] = useState<string[]>([]);
	const [orderBy, setOrderBy] = useState<keyof TestProps>('index');
	const [filterName, setFilterName] = useState('');
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [selectedTestId, setSelectedTestId] = useState<string | number | null>(null);
	const navigate = useNavigate();
	const { data, isLoading } = useGetTestsQuery();
	const [delTestMutation] = useDelTestMutation();

	let emptyRows = 0;
	let filteredData: TestProps[] = [];
	let isNotFound = !filteredData.length && !!filterName;

	const handleOpenMenu = (event: MouseEvent<HTMLElement>, id: string | number) => {
		setMenuOpen(event.currentTarget);
		setSelectedTestId(id);
	};

	const handleCloseMenu = () => {
		setMenuOpen(null);
	};

	const handleDataSort = (event: MouseEvent<unknown>, property: keyof TestProps) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event: MouseEvent<HTMLButtonElement>, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setRowsPerPage(Number.parseInt(event.target.value, 10));
	};

	const handleFilterByName = (event: ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setFilterName(event.target.value);
	};

	const handleDeleteTest = () => {
		delTestMutation(selectedTestId);
		setMenuOpen(null);
	};

	const handleEditTest = () => {
		navigate(`/dashboard/admin/tests/${selectedTestId}/edit`);
		setMenuOpen(null);
	};

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	if (isLoading) {
		return (
			<>
				<Helmet>
					<title>Tests - {process.env.REACT_APP_ORG}</title>
				</Helmet>
				<Container sx={{ width: '75%' }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
						<Typography variant="h4" gutterBottom>
							Tests
						</Typography>
					</Stack>
					<LinearProgress />
				</Container>
			</>
		);
	}

	if (data) {
		emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;
		filteredData = applySortFilter({
			list: data,
			comparator: getComparator(order, orderBy),
			query: filterName,
			orderBy: 'name',
		});
		isNotFound = !filteredData.length && !!filterName;
	}

	return (
		<>
			<Helmet>
				<title>Tests - {process.env.REACT_APP_ORG}</title>
			</Helmet>
			<Container sx={{ width: '75%' }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Typography variant="landing_page_heading" gutterBottom>
						Tests
					</Typography>
					<Button
						variant="contained"
						data-route="/dashboard/admin/tests/new"
						onClick={handleNavigate}
						startIcon={<Iconify icon="eva:plus-fill" />}
					>
						New Test
					</Button>
				</Stack>

				<Card>
					<ListToolbar
						numSelected={selected.length}
						filterName={filterName}
						onFilterName={handleFilterByName}
						placeHolderText={'Test name...'}
						{...(isAdmin && {
							rightContent: (
								<IconButton color="secondary" data-route="/dashboard/admin/tests/reorder" onClick={handleNavigate}>
									<SwapVertIcon />
								</IconButton>
							),
						})}
					/>

					<Scrollbar>
						<TableContainer>
							<Table>
								<ListHead order={order} orderBy={orderBy} headLabel={TABLE_HEAD} onRequestSort={handleDataSort} />
								<TableBody>
									{filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
										const { id, name, desc, price, isBulk } = row;

										return (
											<TableRow hover key={id} tabIndex={-1} selected={selected.indexOf(name) !== -1}>
												<TableCell align="left">
													<Typography variant="subtitle2">{id}</Typography>
												</TableCell>
												<TableCell align="left">{name}</TableCell>
												<TableCell align="left">{desc}</TableCell>
												<TableCell align="left">{fCurrency(price)}</TableCell>
												<TableCell align="left">{isBulk && <Label color="success">BULK</Label>}</TableCell>
												<TableCell align="right">
													<IconButton size="large" color="inherit" onClick={(event) => handleOpenMenu(event, id)}>
														<Iconify icon={'eva:more-vertical-fill'} />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
									{emptyRows > 0 && (
										<TableRow style={{ height: 53 * emptyRows }}>
											<TableCell colSpan={6} />
										</TableRow>
									)}
								</TableBody>

								{isNotFound && (
									<TableBody>
										<TableRow>
											<TableCell align="center" colSpan={6} sx={{ py: 3 }}>
												<Paper
													sx={{
														textAlign: 'center',
													}}
												>
													<Typography variant="h6" paragraph>
														Not found
													</Typography>

													<Typography variant="body2">
														No results found for &nbsp;
														<strong>&quot;{filterName}&quot;</strong>.
														<br /> Check your typos or use complete words
													</Typography>
												</Paper>
											</TableCell>
										</TableRow>
									</TableBody>
								)}
							</Table>
						</TableContainer>
					</Scrollbar>

					<TablePagination
						rowsPerPageOptions={[10, 15, 20]}
						component="div"
						count={data ? data.length : 0}
						rowsPerPage={rowsPerPage}
						page={page}
						onPageChange={handleChangePage}
						onRowsPerPageChange={handleChangeRowsPerPage}
					/>
				</Card>
			</Container>

			<Popover
				open={Boolean(menuOpen)}
				anchorEl={menuOpen}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				PaperProps={{
					sx: {
						p: 1,
						width: 140,
						'& .MuiMenuItem-root': {
							px: 1,
							typography: 'body2',
							borderRadius: 0.75,
						},
					},
				}}
			>
				<MenuItem onClick={handleEditTest}>
					<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
					View/Edit
				</MenuItem>
				<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteTest}>
					<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
					Delete
				</MenuItem>
			</Popover>
		</>
	);
}
